import React from "react";
import styles from "./styles.module.scss";
import UserAvatar from "app/components/UserAvatar";
import SubscriptionCard from "./SubscriptionCard";
import OfferingCard from "./OfferingCard";
import { useTranslation } from "react-i18next";
import SessionSvg from "../../../../../public/images/session.svg";
import { weekdayRestrictionLabel } from "../../NewOffering/utils";

interface RateLink {
  title: string;
  quantity: number;
  duration: string;
  remainingSessions: number;
  // CN-2832: the part of remainingSessions carried over from earlier cycles.
  carriedOverSessions?: number;
  unlimited: boolean;
}

interface Subscription {
  fullName?: string;
  profilePicture: string;
  offeringTitle: string;
  billingFrequency: number | null;
  nextInvoiceDate: string | null;
  // CN-2641: end date of a capped subscription run (null when uncapped).
  subscriptionEndsAt?: string | null;
  expiredAt?: string | null;
  productPrice: number;
  currency: string;
  remainingSessions: number | null;
  unlimited?: boolean;
  productType?: string;
  productId?: string;
  coachId?: string;
  studentProfileId?: string;
  rateLinks?: RateLink[];
  cancelled?: boolean;
  restrictWeekdays?: boolean;
  restrictWeekends?: boolean;
  weekdayBeginAt?: number;
  weekdayEndAt?: number;
}

interface SubscriptionCardWithCoachProps {
  subscription: Subscription;
  coachName: string;
  coachImage: string;
  ownerType?: "Facility" | "CoachProfile";
  // CN-1323 (CFTB-3304): the family member this offering belongs to, shown only
  // on a head's payments page. Null/undefined for self/solo accounts.
  familyMemberName?: string | null;
  formatInvoiceDate: (isoDate: string | null) => string;
  handleDelete: (productId: string) => void;
}

const SubscriptionCardWithCoach: React.FC<SubscriptionCardWithCoachProps> = ({
  subscription,
  coachName,
  coachImage,
  ownerType,
  familyMemberName,
  formatInvoiceDate,
  handleDelete,
}) => {
  const { t } = useTranslation();

  const ownerLabel = ownerType === "Facility"
    ? t("Facility: {{name}}", { name: coachName })
    : t("Coach: {{coachName}}", { coachName });

  return (
    <div className={styles.subscriptionCardWithCoach}>
      <div className={styles.coachInfo}>
        <div className={styles.coachInfo}>
          <UserAvatar
            srcName={coachImage}
            userName={coachName}
            className={styles.avatar2}
            altName="coach-avatar"
            sizeHeight={40}
            sizeWidth={40}
          />
          <span className={styles.coachName}>
            {ownerLabel}
            {familyMemberName && (
              <span className={styles.familyMember}>
                {t("For: {{name}}", { name: familyMemberName })}
              </span>
            )}
          </span>
        </div>
      </div>

      <div className={styles.divider2}></div>
      {subscription.productType === "subscription" ? (
        <SubscriptionCard
          subscription={subscription}
          formatInvoiceDate={formatInvoiceDate}
          handleDelete={handleDelete}
        />
      ) : (
        <OfferingCard subscription={subscription} />
      )}
      {subscription.restrictWeekdays && !subscription.restrictWeekends && (
        <div className={styles.restriction_area}>
          <span className={styles.restriction_badge}>
            {weekdayRestrictionLabel(
              subscription.weekdayBeginAt ?? 0,
              subscription.weekdayEndAt ?? 1440,
              t("Weekdays only"),
            )}
          </span>
        </div>
      )}
      {subscription.restrictWeekends && !subscription.restrictWeekdays && (
        <div className={styles.restriction_area}>
          <span className={styles.restriction_badge}>
            {t("Weekends & Holidays only")}
          </span>
        </div>
      )}
      {subscription.rateLinks?.length > 0 && (
        <div className={styles.rate_links_container}>
          {subscription.rateLinks.map((link, index) => {
            return (
              <div key={index} className={styles.rate_link_wrapper}>
                <div className={styles.rate_link_row}>
                  <div className={styles.rate_link_left}>
                    <img
                      src={SessionSvg}
                      alt="session-icon"
                      className={styles.session_icon}
                    />
                    <span className={styles.rate_link_title}>
                      {link.title ? link.title : t("Remaining Sessions")}
                    </span>
                  </div>
                  <span className={styles.rate_link_quantity}>
                    {link.unlimited ? (<span>&infin;</span>) : link.remainingSessions}{" "}
                    {link.remainingSessions === 1
                      ? t("Session")
                      : t("Sessions")}
                    {/* CN-2832: "8 Sessions (4 carried over)" */}
                    {(link.carriedOverSessions ?? 0) > 0 &&
                      ` ${t("({{count}} carried over)", { count: link.carriedOverSessions })}`}
                  </span>
                </div>
                {index < subscription.rateLinks.length - 1 && (
                  <hr className={styles.divider_line} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SubscriptionCardWithCoach;
